import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  useTeam,
  createTask,
  sendMessage,
  updateTask,
  updateMessage,
  deleteTask,
  getTaskForEdit,
  attachToMessage,
  useTaskTags,
  setMyTaskTags,
  addFollowers,
  useMyMemberGroups,
  toggleReminderDone,
} from './api';
import type { FirstMessage, Tag } from './api';
import { useAuth } from '../auth/AuthProvider';
import { useDialog } from '../ui/Dialogs';
import MessageEditor, { type MessageEditorHandle } from './MessageEditor';
import TagSelector from './TagSelector';
import FollowerPicker from './FollowerPicker';
import PersonSelect from './PersonSelect';

interface Props {
  mode?: 'create' | 'edit';
}

// Datos que llegan al convertir un recordatorio en hilo (ver AgendaPage → ReminderForm).
interface ConvertPayload {
  reminderId: string;
  titulo: string;
  fecha: string;
  nota: string | null;
  tags: Tag[];
}

export default function TareaFormPage({ mode = 'create' }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();
  const { confirm } = useDialog();
  const { team } = useTeam();
  const { id: routeId } = useParams<{ id: string }>();

  const isEdit = mode === 'edit';
  // Precarga al convertir un recordatorio (solo en modo creación).
  const fromReminder = !isEdit
    ? ((location.state as { fromReminder?: ConvertPayload } | null)?.fromReminder ?? null)
    : null;

  // ID estable para los uploads del editor — en create generamos uno nuevo
  const taskId = useMemo(() => (isEdit && routeId ? routeId : crypto.randomUUID()), [isEdit, routeId]);
  const editorRef = useRef<MessageEditorHandle>(null);

  const [loading, setLoading] = useState(isEdit);
  const [asunto, setAsunto] = useState(fromReminder?.titulo ?? '');
  const [assignedTo, setAssignedTo] = useState('');
  const [fecha, setFecha] = useState(fromReminder?.fecha ?? '');
  const [firstMessage, setFirstMessage] = useState<FirstMessage | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [tags, setTags] = useState<Tag[]>(fromReminder?.tags ?? []);
  // La nota del recordatorio se precarga como primer mensaje del hilo.
  const initialBody = fromReminder?.nota ?? '';
  // Seguidores opcionales al crear el hilo (solo en modo creación; en edición
  // se gestionan desde el propio hilo).
  const [followerIds, setFollowerIds] = useState<string[]>([]);
  const { groups: myGroups } = useMyMemberGroups(profile?.id);
  // En modo edit pre-cargamos las tags que YO ya tengo aplicadas al hilo.
  const { tags: existingTags } = useTaskTags(isEdit ? routeId : undefined, profile?.id);
  useEffect(() => {
    if (isEdit) setTags(existingTags);
  }, [isEdit, existingTags]);

  useEffect(() => {
    if (!isEdit || !routeId) return;
    let cancelled = false;
    (async () => {
      const res = await getTaskForEdit(routeId);
      if (cancelled || !res) return;
      setAsunto(res.task.asunto ?? '');
      setAssignedTo(res.task.assigned_to ?? '');
      setFecha(res.task.fecha_vencimiento ?? '');
      setFirstMessage(res.firstMessage);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [isEdit, routeId]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!asunto.trim() || !profile) return;
    if (!assignedTo) { setError('Asigna el hilo a una persona del equipo.'); return; }
    setBusy(true);
    setError('');
    try {
      if (isEdit) {
        await updateTask(taskId, {
          asunto: asunto.trim(),
          assigned_to: assignedTo || null,
          fecha_vencimiento: fecha || null,
        });

        const editor = editorRef.current;
        if (editor && !editor.isEmpty()) {
          const { body, attachments } = editor.getContent();
          if (firstMessage) {
            await updateMessage(firstMessage.id, { body });
            await attachToMessage(firstMessage.id, attachments, profile.id);
          } else {
            await sendMessage({ task_id: taskId, author_id: profile.id, body, attachments });
          }
        }

        await setMyTaskTags(taskId, tags.map((t) => t.id), profile.id);

        navigate(`/tareas/${taskId}`, { replace: true });
      } else {
        await createTask({
          id: taskId,
          asunto: asunto.trim(),
          assigned_to: assignedTo || null,
          fecha_vencimiento: fecha || null,
          created_by: profile.id,
        });

        const editor = editorRef.current;
        if (editor && !editor.isEmpty()) {
          const { body, attachments } = editor.getContent();
          await sendMessage({ task_id: taskId, author_id: profile.id, body, attachments });
        }

        if (tags.length > 0) {
          await setMyTaskTags(taskId, tags.map((t) => t.id), profile.id);
        }

        if (followerIds.length > 0) {
          await addFollowers(taskId, followerIds);
        }

        // Si venimos de un recordatorio, lo cerramos (marcamos como hecho) ahora
        // que el hilo ya existe. Un fallo aquí no debe impedir abrir el hilo.
        if (fromReminder) {
          try {
            await toggleReminderDone(fromReminder.reminderId, true);
          } catch (err) {
            console.error('No se pudo cerrar el recordatorio tras convertirlo', err);
          }
        }

        navigate(`/tareas/${taskId}`, { replace: true });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al guardar';
      setError(msg);
      setBusy(false);
    }
  };

  const onCancel = () => {
    if (isEdit) navigate(`/tareas/${taskId}`);
    else navigate('/bandeja');
  };

  const onDelete = async () => {
    if (!isEdit) return;
    const ok = await confirm({
      title: 'Eliminar hilo',
      message: 'Se borrarán también todos sus mensajes y archivos adjuntos. No se puede deshacer.',
      confirmLabel: 'Eliminar hilo',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    setError('');
    try {
      await deleteTask(taskId);
      navigate('/bandeja', { replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al eliminar';
      setError(msg);
      setBusy(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, color: '#64748b' }}>Cargando…</div>;
  }

  return (
    <div style={{ padding: '32px 40px', maxWidth: 720, margin: '0 auto' }}>
      <button
        type="button"
        onClick={onCancel}
        style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 12 }}
      >← Volver{isEdit ? ' al hilo' : ' a Bandeja'}</button>

      <h1 style={{ fontSize: 26, fontWeight: 700, color: '#0f172a', margin: 0 }}>
        {isEdit ? 'Editar hilo' : fromReminder ? 'Convertir recordatorio en hilo' : 'Nuevo hilo'}
      </h1>
      <p style={{ color: '#64748b', fontSize: 14, marginTop: 4, marginBottom: 22 }}>
        {isEdit
          ? 'Cambia el asunto, la asignación, la fecha o el primer mensaje. Los cambios se guardan al pulsar el botón.'
          : fromReminder
            ? 'Hemos traído el título, la fecha, la nota y las tags del recordatorio. Asigna un responsable y completa lo que falte. Al crear el hilo, el recordatorio se marcará como hecho.'
            : 'Pon un asunto claro, decide a quién se asigna y, si quieres, escribe el primer mensaje con formato, imágenes o archivos.'}
      </p>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <label style={styles.label}>
          <span style={styles.labelText}>Asunto</span>
          <textarea
            value={asunto}
            onChange={(e) => setAsunto(e.target.value)}
            placeholder="¿De qué va el tema?"
            rows={2}
            autoFocus
            required
            className="st-field"
            style={{ ...styles.input, resize: 'vertical', fontFamily: 'inherit' }}
          />
        </label>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ ...styles.label, flex: 1, minWidth: 220 }}>
            <span style={styles.labelText}>Asignar a</span>
            <PersonSelect
              value={assignedTo}
              onChange={setAssignedTo}
              members={team}
              placeholder="— Elige una persona —"
            />
          </div>

          <label style={{ ...styles.label, flex: 1, minWidth: 220 }}>
            <span style={styles.labelText}>Fecha de vencimiento (opcional)</span>
            <input
              type="date"
              className="st-field"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              style={styles.input}
            />
          </label>
        </div>

        {!isEdit && (
          <div style={styles.label}>
            <span style={styles.labelText}>Seguidores (opcional)</span>
            <FollowerPicker
              value={followerIds}
              onChange={setFollowerIds}
              members={team}
              groups={myGroups}
              placeholder="+ Etiquetar a alguien del equipo"
            />
            <span style={{ fontSize: 12, color: '#94a3b8' }}>
              Seguirán el hilo y recibirán sus avisos, sin ser los responsables.
            </span>
          </div>
        )}

        <div style={styles.label}>
          <span style={styles.labelText}>
            Primer mensaje {isEdit && firstMessage ? '(se actualizará al guardar)' : '(opcional)'}
          </span>
          <MessageEditor
            ref={editorRef}
            taskId={taskId}
            showSendButton={false}
            minHeight={120}
            placeholder="Contexto, instrucciones, enlaces, imágenes, adjuntos…"
            initialContent={firstMessage?.body ?? initialBody}
            onSend={() => {}}
          />
        </div>

        {error && <p style={{ color: '#dc2626', fontSize: 13, margin: 0 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 4 }}>
          {isEdit && (
            <button type="button" onClick={onDelete} disabled={busy} style={styles.btnDanger}>
              Eliminar hilo
            </button>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <TagSelector selected={tags} onChange={setTags} />
          </div>
          <button type="button" onClick={onCancel} style={styles.btnGhost}>Cancelar</button>
          <button type="submit" disabled={busy} style={styles.btnPrimary}>
            {busy ? 'Guardando…' : isEdit ? 'Editar hilo' : 'Crear hilo'}
          </button>
        </div>
      </form>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  label: { display: 'flex', flexDirection: 'column', gap: 6 },
  labelText: { fontSize: 13, fontWeight: 600, color: '#475569' },
  input: {
    border: '1px solid #e2e8f0',
    borderRadius: 10,
    padding: '11px 13px',
    fontSize: 14,
    outline: 'none',
    background: '#fff',
  },
  btnGhost: {
    background: 'transparent',
    border: '1px solid #cbd5e1',
    borderRadius: 8,
    padding: '9px 18px',
    cursor: 'pointer',
    color: '#475569',
    fontSize: 14,
    fontWeight: 600,
  },
  btnPrimary: {
    background: '#16a34a',
    border: 'none',
    borderRadius: 8,
    padding: '10px 24px',
    cursor: 'pointer',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
  },
  btnDanger: {
    background: 'transparent',
    border: '1px solid #fecaca',
    borderRadius: 8,
    padding: '9px 16px',
    cursor: 'pointer',
    color: '#dc2626',
    fontSize: 13,
    fontWeight: 600,
  },
};
